import { computed, ref, watch, type Ref } from "vue";
import PeriodDataDTO from "@shared-dto/workout/period.data.dto";
import { getUserPeriodData } from "@shared-services/workout/period.data.service";
import useWorkoutTypesFilter from "@shared-composables/workout/useWorkoutTypesFilter";
import useMovementCategoryFilter from "@shared-composables/movement/useMovementCategoryFilter";
import useToastError from "@shared-composables/useToastError";

const EMPTY = (): PeriodDataDTO => ({ counts: [], totalSessions: 0, stats: {}, scoredMovements: [], muscleLoad: [], bodyPartLoad: [] });

// Cache module-scope par (period|iso|types) → survit au remount (HMR, changement de pane, back).
const cache = new Map<string, PeriodDataDTO>();
// Requêtes en vol par clé → summary (pane 1) et period-stats (pane 2) partagent UN seul appel HTTP.
const inFlight = new Map<string, Promise<PeriodDataDTO>>();

/**
 * Source UNIQUE des données d'une période (semaine/mois/année) — un seul appel `/workouts/period-data`.
 * Réactif au filtre par type (clic sur un workout-type du summary) : `counts` reste tous types,
 * `stats`/`movements`/`totalSessions` sont filtrés. `injected` = mode démo non-connecté (données figées).
 */
export default function useWorkoutsPeriod(
	period: Ref<"week" | "month" | "year" | "total">,
	iso: Ref<string>,
	options?: { injected?: Ref<PeriodDataDTO | null> },
)
{
	const { selectedAliases: workoutTypeAliases } = useWorkoutTypesFilter();
	const { selectedAliases: movementCategoryAliases } = useMovementCategoryFilter();
	const data = ref<PeriodDataDTO>(EMPTY());
	const injected = options?.injected;
	// Vrai pendant un appel API réel (cache miss). Sert au spinner des boutons de navigation période/vue.
	const isFetching = ref<boolean>(false);

	const cacheKey = (): string =>
		`${ period.value }|${ iso.value }|${ [ ...workoutTypeAliases.value ].sort().join(",") }|${ [ ...movementCategoryAliases.value ].sort().join(",") }`;

	const fetchData = async (): Promise<void> =>
	{
		const key = cacheKey();
		const cached = cache.get(key);
		if (cached)
		{
			data.value = cached;
			return;
		}
		// Déduplication : si un fetch pour cette clé est déjà en vol, on partage sa promesse.
		let promise = inFlight.get(key);
		if (! promise)
		{
			promise = getUserPeriodData(period.value, iso.value, workoutTypeAliases.value, movementCategoryAliases.value)
				.then((fresh) => { cache.set(key, fresh); return fresh; })
				.finally(() => inFlight.delete(key));
			inFlight.set(key, promise);
		}
		isFetching.value = true;
		try
		{
			data.value = await promise;
		}
		catch (e)
		{
			useToastError(e);
			data.value = EMPTY();
		}
		finally
		{
			isFetching.value = false;
		}
	};

	const load = (): void =>
	{
		if (injected?.value)
		{
			data.value = injected.value;
			return;
		}
		void fetchData();
	};

	watch([ period, iso, workoutTypeAliases, movementCategoryAliases ], load, { immediate: true });
	if (injected)
	{
		watch(injected, (v) => { if (v) { data.value = v; } }, { deep: true });
	}

	return {
		isFetching: computed<boolean>(() => isFetching.value),
		counts: computed(() => data.value.counts),
		stats: computed(() => data.value.stats),
		scoredMovements: computed(() => data.value.scoredMovements ?? []),
		durationBuckets: computed(() => data.value.durationBuckets ?? []),
		muscleLoad: computed(() => data.value.muscleLoad ?? []),
		bodyPartLoad: computed(() => data.value.bodyPartLoad ?? []),
		periodChart: computed(() => data.value.periodChart ?? null),
		sessionsTotal: computed(() => data.value.sessionsTotal ?? null),
	};
}

/** Vidé à la déconnexion : la clé du cache ne porte pas l'utilisateur, le suivant verrait les séances du précédent. */
export function resetWorkoutsPeriodCache(): void
{
	cache.clear();
	inFlight.clear();
}
